import { useLayoutEffect, useId, useRef, useState } from 'react';
import { useAppRightPanel } from '../contexts/AppRightPanelContext';
import { useMediaQuery } from './useMediaQuery';

/** Claim the existing app-shell inspector without moving feature state into the shell. */
export function useShellInspector(open: boolean, onClose: () => void, side: 'left' | 'right' = 'right') {
  const id = useId();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [host, setHost] = useState<HTMLElement | null>(null);
  const { claimShellInspector, releaseShellInspector, activeShellInspectorId, claimLeftShellInspector, releaseLeftShellInspector, activeLeftShellInspectorId } = useAppRightPanel();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useLayoutEffect(() => {
    if (!isDesktop) {
      setHost(null);
      return;
    }
    const findHost = () => {
      const next = document.getElementById(side === 'left' ? 'app-left-panel-portal' : 'app-right-panel-portal');
      setHost(current => current === next ? current : next);
    };
    findHost();
    // The shell and feature respond to viewport changes independently.
    const observer = new MutationObserver(findHost);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isDesktop, side]);
  const usesShell = isDesktop && host !== null && host.isConnected;
  useLayoutEffect(() => {
    if (!open || !usesShell) return;
    const claim = side === 'left' ? claimLeftShellInspector : claimShellInspector;
    const release = side === 'left' ? releaseLeftShellInspector : releaseShellInspector;
    claim(id, () => closeRef.current());
    return () => release(id);
  }, [open, usesShell, host, id, side, claimShellInspector, releaseShellInspector, claimLeftShellInspector, releaseLeftShellInspector]);
  return { host: usesShell ? host : null, active: (side === 'left' ? activeLeftShellInspectorId : activeShellInspectorId) === id, usesShell };
}
